import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { priceToString } from '../../utils/index';

import InputText from '../common/InputText';
import messages from './messages';
import styles from './EstimatedTaxCalculator.module.scss';
import * as errorCodes from './constants';
import { apiErrorCodeConstantMap } from './constants';
import {
  getTaxEstimation,
  removeTaxEstimation,
} from '../../slices/cart';
// import useGetUserSessionInfo from '../../hooks/queries/useGetUserSessionInfo';

const getErrorMessage = (errorCode) => {
  const messageConstant = apiErrorCodeConstantMap[errorCode]
    || errorCodes.SERVICE_FAILED;

  return messages[messageConstant];
};

const EstimatedTaxCalculator = ({ showEstimatedTaxCalculator, handleShowingTaxClick }) => {
  const dispatch = useDispatch();
  const { formatMessage, formatNumber } = useIntl();
  const {
    estimatedTaxZipCode,
    estimatedTaxAmount,
    estimatedTaxErrorCode,
    lineItems,
  } = useSelector((state) => state?.cart);

  const [btnAddExpanded, setBtnAddExpanded] = useState(showEstimatedTaxCalculator || false);
  const [inputZipCode, setInputZipCode] = useState('');
  const inputZipCodeRef = useRef(null);

  const toggleFormTextTpl = useMemo(() => {
    if (btnAddExpanded) {
      return formatMessage(messages.addZipCodeButtonHide);
    }

    return formatMessage(messages.addZipCodeButtonAdd)
  }, [btnAddExpanded, formatMessage]);

  const errorMessage = useMemo(() => formatMessage(
    getErrorMessage(estimatedTaxErrorCode),
  ), [estimatedTaxErrorCode, formatMessage]);

  const toggleForm = useCallback(() => {
    const nextState = !btnAddExpanded;
    setBtnAddExpanded(() => nextState);

    handleShowingTaxClick(nextState);

    if (nextState) {
      inputZipCodeRef.current?.focus();
    }
  }, [btnAddExpanded, handleShowingTaxClick]);

  const handleZipCodeInput = (event) => {
    const inputValueMasked = event.target.value.replace(/[^\d\s]/gi, '');
    setInputZipCode(inputValueMasked.substring(0, 5));
  };

  const handleFormSubmit = useCallback(async (event) => {
    event.preventDefault();
    const zipCode = inputZipCodeRef.current.value;

    if (!zipCode?.length) {
      dispatch({
        type: 'addTaxEstimationError',
        errorCode: errorCodes.ZIP_CODE_EMPTY_CODE,
      });
      return;
    }

    if (zipCode?.length < 5) {
      dispatch({
        type: 'addTaxEstimationError',
        errorCode: errorCodes.TAXES_NOT_FOUND_CODE,
      });
      return;
    }

    const result = await dispatch(getTaxEstimation({ zipCode }));
    if (!result.error && result.payload) {
      setInputZipCode(result.payload.zipCode);
      setBtnAddExpanded(false);
    }
  }, [dispatch]);

  // This is disabled because it creates a weird situation where the total price
  // of the order shows as the tax (since the zip code is prefilled before the
  // page even loads, before the user gets to it).
  // We fixed the issue with updating tax from Express zip code, so this isn't
  // needed as much.

  // Get zip code from geolocation, so we can estimate taxes without asking the
  // user.
  // const userSessionInfo = useGetUserSessionInfo();
  // const geoZipCode = userSessionInfo?.data?.postalCode;
  //
  // useEffect(() => {
  //   if (geoZipCode) {
  //     dispatch(getTaxEstimation({ zipCode: geoZipCode }));
  //   }
  // }, [dispatch, geoZipCode]);


  useEffect(() => {
    dispatch(removeTaxEstimation());
  }, [dispatch, lineItems]);

  useEffect(() => {
    if (btnAddExpanded) {
      inputZipCodeRef.current?.focus();
    }
  }, [btnAddExpanded]);

  return (
    <>
      <div className={styles['estimated-tax-container']}>
        <div className={styles['estimated-tax-row']}>
          <div>{formatMessage(messages.mainTitle)}</div>
            <div
            className={styles['toggle-button']}
            data-testid="add-zip-code-button"
            role="button"
            tabIndex={0}
            onClick={toggleForm}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                toggleForm();
              }
            }}
          >
            {estimatedTaxZipCode || inputZipCode
              ? (estimatedTaxZipCode || inputZipCode)
              : toggleFormTextTpl}
          </div>
          {estimatedTaxAmount
            ? <div>
                {priceToString({
                  price: estimatedTaxAmount,
                  formatNumber,
                  formatMessage,
                  forceDigit: true,
                })}
              </div>
            : ''}
        </div>
        {btnAddExpanded &&
        <form
          className={classnames(
            styles.form,
            !btnAddExpanded ? styles['form--hide'] : '',
          )}
          onSubmit={handleFormSubmit}
        >
          <InputText
            value={inputZipCode}
            name="txt-zip-code"
            id="txt-zip-code"
            ref={inputZipCodeRef}
            label={formatMessage(messages.txtZipCodePlaceholder)}
            onChange={handleZipCodeInput}
            onBlur={handleZipCodeInput}
            buttonText={formatMessage(messages.btnSubmitValue)}
            isValid={!estimatedTaxErrorCode}
            errorMsg={estimatedTaxErrorCode ? errorMessage : ''}
          />
        </form>
        }
      </div>

    </>
  );
};
EstimatedTaxCalculator.propTypes = {
  showEstimatedTaxCalculator: PropTypes.bool,
  handleShowingTaxClick: PropTypes.func.isRequired,
};
EstimatedTaxCalculator.defaultProps = {
  showEstimatedTaxCalculator: false,
};

export default EstimatedTaxCalculator;
